"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { FaFacebookF, FaInstagram, FaGoogle, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";

const Footer: React.FC = () => {
  return (
    <footer
      className="text-gray-700 py-8"
      role="contentinfo"
      aria-label="Smile4Me Footer"
    >
      <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-center md:items-start md:text-left">
        {/* Left: Badge Images */}
        <div className="flex items-center justify-center space-y-4 mb-6 md:mb-0">
          <div className="w-[170px] h-[134px] lg:w-[170px] lg:h-[134px] relative">
            <Image
              src="/images/custom_images_bestdentists2024.webp"
              alt="America's Best Dentists 2024"
              fill
              className="object-contain"
              sizes="(max-width: 1024px) 170px, 170px"
            />
          </div>
          <div className="w-[170px] h-[134px] lg:w-[170px] lg:h-[134px] relative">
            <Image
              src="/images/custom_images_invisalign2024b.webp"
              alt="Invisalign Provider 2024"
              fill
              className="object-contain"
              sizes="(max-width: 1024px) 170px, 170px"
            />
          </div>
        </div>

        {/* Middle: Contact Us */}
        <div className="mb-6 md:mb-0">
          <h3
            className="text-3xl font-semibold mb-4 after:content-[''] after:block after:w-[300px] after:h-[3px] after:my-5 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]"
          >
            Contact Us
          </h3>
          <address
            className="space-y-2 text-gray-800 not-italic"
            aria-label="Contact information for Smile4Me"
          >
            <p className="flex items-center justify-center md:justify-start font-semibold">
              <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-1 inline-flex items-center justify-center">
                <FaPhoneAlt className="text-white text-base" />
              </span>
              <span className="ml-2">Phone</span>
            </p>
            <p className="flex items-center justify-center md:justify-start">
              <Link
                href="tel:+17184004400"
                className="text-gray-800 hover:text-[#AE7D2F] transition-colors duration-300"
                aria-label="Call Smile4Me at (718) 400-4400"
              >
                (718) 400-4400
              </Link>
            </p>
            <p className="flex items-center justify-center md:justify-start font-semibold">
              <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-1 inline-flex items-center justify-center">
                <FaMapMarkerAlt className="text-white text-base" />
              </span>
              <span className="ml-2">Address</span>
            </p>
            <p className="flex items-center justify-center md:justify-start">
              <Link
                href="https://maps.app.goo.gl/GcexFskLt3MwefMR9"
                className="text-gray-800 hover:text-[#AE7D2F] transition-colors duration-300"
                aria-label="View Smile4Me location on Google Maps"
                target="_blank"
                rel="noopener noreferrer"
              >
                31-57 Steinway St, Queens, NY 11103
              </Link>
            </p>
            <p className="font-semibold">Social</p>
            <div className="flex gap-3 justify-center md:justify-start mt-2">
              <Link
                href="https://www.facebook.com/profile.php?id=100090912068381"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Facebook"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaFacebookF className="text-white text-base" />
                </span>
              </Link>
              <Link
                href="https://www.instagram.com/smile4mecare/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Instagram"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaInstagram className="text-white text-base" />
                </span>
              </Link>
              <Link
                href="https://maps.app.goo.gl/GcexFskLt3MwefMR9"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#ECD05B] transition-colors duration-300"
                aria-label="Visit Smile4Me on Google Business"
              >
                <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                  <FaGoogle className="text-white text-base" />
                </span>
              </Link>
            </div>
          </address>
        </div>

        {/* Right: Navigation Links */}
        <div>
          <h3
            className="text-3xl font-semibold mb-4 after:content-[''] after:block after:w-[300px] after:h-[3px] after:my-5 after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]"
          >
            Navigation
          </h3>
          <div className="grid grid-cols-2 gap-x-8 mx-auto">
            {/* Left column */}
            <ul className="space-y-2">
              <li>
                <Link
                  href="/"
                  className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
                  aria-label="Navigate to Home page"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  href="/our-office"
                  className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
                  aria-label="Navigate to About page"
                >
                  About Our Office
                </Link>
              </li>
              <li>
                <Link
                  href="/services"
                  className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
                  aria-label="Navigate to Services page"
                >
                  Services
                </Link>
              </li>
            </ul>

            {/* Right column */}
            <ul className="space-y-2">
              <li>
                <Link
                  href="/blog"
                  className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
                  aria-label="Navigate to Blog page"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
                  aria-label="Navigate to Contact page"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Content */}
      <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col items-center text-center container mx-auto px-6">
        <p className="text-gray-700 text-base">
          <Link
            href="/accessibility/"
            className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
            aria-label="Accessibility Policy page"
            target="_blank"
            rel="noopener noreferrer"
          >
            Accessibility Policy
          </Link>
        </p>
        <p className="text-gray-700 text-base mt-2">
          © 2024 smile4me. All Rights Reserved.
        </p>
        <p className="text-gray-700 text-base mt-2">
          <Link
            href="https://www.excaliburinteractive.org/"
            className="text-gray-700 hover:text-[#AE7D2F] transition-colors duration-300"
            aria-label="Excalibur Interactive website"
            target="_blank"
            rel="noopener noreferrer"
          >
            Developed and Maintained by Excalibur Interactive
          </Link>
        </p>
        <p className="text-gray-700 text-base mt-2">
          Astoria Dentist | Dentist 11103 | Cosmetic Dentist in Astoria
        </p>
      </div>
    </footer>
  );
};

export default Footer;